import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { MarketingNav } from "@/components/marketing/MarketingNav";
// integrator: may need vite server.fs.allow for repo root — this imports a
// file that lives outside apps/web (the repo-root CHANGELOG.md).
import changelogRaw from "../../../../CHANGELOG.md?raw";
import styles from "./Changelog.module.css";

/**
 * Renders the real repo-root CHANGELOG.md. We deliberately do NOT pull in a
 * markdown library — the changelog only uses a small subset of markdown, so a
 * tiny inline renderer covers it:
 *   - `# ...`  → h1
 *   - `## ...` → h2 (date section)
 *   - `- ...`  → bullet list (continuation lines indented under a bullet are
 *               folded back into that bullet)
 *   - blank-line-separated runs of plain text → paragraphs
 */

type Block =
  | { type: "h1"; text: string }
  | { type: "h2"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "p"; text: string };

function parseChangelog(src: string): Block[] {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];

  let listItems: string[] | null = null;
  let paraLines: string[] | null = null;

  const flushList = () => {
    if (listItems && listItems.length) blocks.push({ type: "ul", items: listItems });
    listItems = null;
  };
  const flushPara = () => {
    if (paraLines && paraLines.length) {
      blocks.push({ type: "p", text: paraLines.join(" ").trim() });
    }
    paraLines = null;
  };

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, "");
    const trimmed = line.trim();

    if (trimmed === "") {
      // Blank line ends a paragraph (but list continuation handled separately).
      flushPara();
      continue;
    }

    const h2 = /^##\s+(.*)$/.exec(trimmed);
    if (h2) {
      flushList();
      flushPara();
      blocks.push({ type: "h2", text: h2[1] ?? "" });
      continue;
    }

    const h1 = /^#\s+(.*)$/.exec(trimmed);
    if (h1) {
      flushList();
      flushPara();
      blocks.push({ type: "h1", text: h1[1] ?? "" });
      continue;
    }

    const bullet = /^-\s+(.*)$/.exec(trimmed);
    if (bullet) {
      flushPara();
      if (!listItems) listItems = [];
      listItems.push(bullet[1] ?? "");
      continue;
    }

    // A non-blank, non-marker line: if we're inside a list, it's a wrapped
    // continuation of the last bullet. Otherwise it's paragraph text.
    if (listItems && listItems.length) {
      listItems[listItems.length - 1] = `${listItems[listItems.length - 1]} ${trimmed}`.trim();
      continue;
    }
    if (!paraLines) paraLines = [];
    paraLines.push(trimmed);
  }

  flushList();
  flushPara();
  return blocks;
}

const BLOCKS = parseChangelog(changelogRaw);

export function Changelog() {
  // The first h1 is the document title; render it in the hero, not the body.
  const titleBlock = BLOCKS.find((b) => b.type === "h1");
  const title = titleBlock && titleBlock.type === "h1" ? titleBlock.text : "Changelog";

  return (
    <>
      <MarketingNav />

      <main className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <p className={styles.kicker}>Changelog</p>
            <h1 className={styles.title}>{title}</h1>
            <p className={styles.lead}>
              A running log of what we've shipped, newest first. This page
              renders the project's real CHANGELOG.md.
            </p>
          </div>
        </section>

        <section className={styles.bodySection}>
          <div className={styles.body}>
            {BLOCKS.map((block, i) => {
              if (block.type === "h1") return null; // shown in hero
              if (block.type === "h2") {
                return (
                  <h2 className={styles.date} key={i}>
                    {block.text}
                  </h2>
                );
              }
              if (block.type === "ul") {
                return (
                  <ul className={styles.list} key={i}>
                    {block.items.map((item, j) => (
                      <li className={styles.item} key={j}>
                        {item}
                      </li>
                    ))}
                  </ul>
                );
              }
              return (
                <p className={styles.para} key={i}>
                  {block.text}
                </p>
              );
            })}
          </div>
        </section>
      </main>

      <MarketingFooter />
    </>
  );
}

export default Changelog;
